import { BookOpen, HeartHandshake, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { JSX, ReactNode } from 'react';

/**
 * The first thing a signed-in person sees.
 *
 * Typography note (docs/09_DESIGN_SYSTEM.md): the Arabic below is rendered
 * with the platform's own system fonts and an explicit `lang="ar"
 * dir="rtl"`, not a loaded Quran webfont. docs/09 puts dedicated Quran
 * typography out of scope and forbids adding a custom web font, and this
 * product must stay fast on a low-end Android over a slow connection - so
 * this is a deliberate constraint being honored, not an oversight. The
 * Bismillah is set large enough that a system Arabic face stays legible.
 *
 * The hadith is `خَيْرُكُمْ مَنْ تَعَلَّمَ الْقُرْآنَ وَعَلَّمَهُ` (HR. Bukhari) rather than a
 * Qur'anic ayah, on purpose: the project's Qur'an text licensing with
 * Kemenag/LPMQ is still unresolved (docs/11), so no ayah text is shipped
 * anywhere in this client until it is settled.
 */

const BISMILLAH = 'بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ';
const HADITH_ARABIC = 'خَيْرُكُمْ مَنْ تَعَلَّمَ الْقُرْآنَ وَعَلَّمَهُ';
const HADITH_TRANSLATION =
  'Sebaik-baik kalian adalah orang yang mempelajari Al-Qur’an dan mengajarkannya.';
const HADITH_SOURCE = 'HR. Bukhari';

interface Pillar {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly description: string;
}

/** The product owner's own three verbs: membaca, menghafal, mengamalkan. */
const PILLARS: readonly Pillar[] = [
  {
    icon: BookOpen,
    title: 'Membaca',
    description:
      'Mulai dari satu ayat hari ini. Yang rutin lebih jauh sampainya daripada yang ramai sesaat.',
  },
  {
    icon: Sparkles,
    title: 'Menghafal',
    description: 'Hafalan tumbuh dari pengulangan yang sabar, bukan dari kejaran yang tergesa.',
  },
  {
    icon: HeartHandshake,
    title: 'Mengamalkan',
    description: 'Yang dihafal dijaga dengan diamalkan - di situlah Al-Qur’an benar-benar tinggal.',
  },
];

export function WelcomeHero({
  fullName,
  subtitle,
  children,
}: {
  readonly fullName: string | null;
  /** One short line of context under the greeting (role, or onboarding state). */
  readonly subtitle: ReactNode;
  /** Optional actions rendered under the greeting. */
  readonly children?: ReactNode;
}): JSX.Element {
  return (
    <header className="flex flex-col gap-6 rounded-lg border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-accent/20 p-6 shadow-raised ring-1 ring-inset ring-white/60 sm:p-8">
      <div className="flex flex-col items-center gap-3 text-center">
        <p
          lang="ar"
          dir="rtl"
          className="text-2xl leading-relaxed text-primary sm:text-3xl"
          // The transliteration is the accessible name so a screen reader
          // in an Indonesian voice does not read the Arabic as gibberish.
          aria-label="Bismillahirrahmanirrahim"
        >
          {BISMILLAH}
        </p>
        <p className="text-xs text-muted-foreground">
          Dengan menyebut nama Allah Yang Maha Pengasih lagi Maha Penyayang
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-semibold">
          Selamat datang{fullName !== null ? `, ${fullName}` : ''}
        </h1>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {subtitle}
        </div>
        {children}
      </div>

      <figure className="flex flex-col gap-2 rounded-md border border-border/60 bg-card/70 p-4">
        <p lang="ar" dir="rtl" className="text-lg leading-loose sm:text-xl">
          {HADITH_ARABIC}
        </p>
        <blockquote className="text-sm leading-relaxed text-muted-foreground">
          “{HADITH_TRANSLATION}”
        </blockquote>
        <figcaption className="text-xs text-muted-foreground">{HADITH_SOURCE}</figcaption>
      </figure>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {PILLARS.map((pillar) => (
          <div
            key={pillar.title}
            className="flex flex-col gap-1.5 rounded-md border border-border/60 bg-card/70 p-3"
          >
            <span className="flex items-center gap-2 text-sm font-medium">
              <span
                className="flex size-7 shrink-0 items-center justify-center rounded-md bg-secondary text-primary"
                aria-hidden="true"
              >
                <pillar.icon className="size-3.5" />
              </span>
              {pillar.title}
            </span>
            <span className="text-xs leading-relaxed text-muted-foreground">
              {pillar.description}
            </span>
          </div>
        ))}
      </div>
    </header>
  );
}
